import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Film, CheckCircle, Crown, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionSuccess = () => {
  const [searchParams] = useSearchParams();
  const { updateUser, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [purchasedTier, setPurchasedTier] = useState('pro');
  
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('moviebot_token');
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          setStatus('success');
          // Get actual tier from response
          const tier = data.tier || 'basic';
          setPurchasedTier(tier);
          // Update user subscription tier in context
          updateUser({ subscription_tier: tier });
          // Also refresh from server to ensure we have latest data
          await refreshUser();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full text-center"
      >
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-10">
          <Film className="w-8 h-8 text-primary" />
          <span className="font-black text-xl tracking-tight text-white">MOVIEBOT</span>
        </div>

        {status === 'checking' && (
          <div className="p-10 bg-secondary/30 border border-white/5">
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
          </div>
        )}

        {status === 'success' && (
          <div className="p-10 bg-primary/10 border-2 border-primary">
            <div className="w-20 h-20 bg-primary flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-black" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-muted-foreground mb-8">
              Welcome to MovieBot {purchasedTier.charAt(0).toUpperCase() + purchasedTier.slice(1)}! Your subscription is now active.
            </p>
            <div className="flex items-center justify-center gap-2 mb-8 p-4 bg-black/30">
              <Crown className="w-6 h-6 text-primary" />
              <span className="text-primary font-bold uppercase tracking-wider">{purchasedTier} Member</span>
            </div>
            <Link to="/dashboard">
              <Button 
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase tracking-widest text-xs py-4"
                data-testid="success-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="p-10 bg-red-500/10 border border-red-500/30">
            <h2 className="text-2xl font-bold text-white mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-8">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <Link to="/subscription">
              <Button 
                className="w-full bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-widest text-xs py-4"
                data-testid="error-retry-btn"
              >
                Try Again
              </Button>
            </Link>
          </div>
        )}

        {status === 'timeout' && (
          <div className="p-10 bg-yellow-500/10 border border-yellow-500/30">
            <h2 className="text-2xl font-bold text-white mb-2">Verification Timeout</h2>
            <p className="text-muted-foreground mb-8">
              Payment verification is taking longer than expected. Please check your email for confirmation.
            </p>
            <Link to="/dashboard">
              <Button 
                className="w-full bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-widest text-xs py-4"
                data-testid="timeout-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </Link>
          </div>
        )}

        {status === 'expired' && (
          <div className="p-10 bg-red-500/10 border border-red-500/30">
            <h2 className="text-2xl font-bold text-white mb-2">Session Expired</h2>
            <p className="text-muted-foreground mb-8">
              Your checkout session has expired. Please try again.
            </p>
            <Link to="/subscription">
              <Button 
                className="w-full bg-white/10 text-white hover:bg-white/20 font-bold uppercase tracking-widest text-xs py-4"
                data-testid="expired-retry-btn"
              >
                Return to Pricing
              </Button>
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default SubscriptionSuccess;
